export const TOOL_LOAD_TRACE_TIMEOUT_MS = 30_000;
export const TOOL_LOAD_MAX_ROUTE_COMMITS = 16;
export const TOOL_LOAD_RUNNER_SESSION_KEY = "upside:tool-load-runner";

export function resolveToolLoadClientObservability(value: string | undefined): boolean {
  return value !== "false";
}

export const TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED = resolveToolLoadClientObservability(
  process.env.NEXT_PUBLIC_TOOL_LOAD_OBSERVABILITY_ENABLED,
);

export type ToolLoadClientObservabilityMode = "enabled" | "disabled";

export const TOOL_LOAD_CLIENT_OBSERVABILITY_MODE: ToolLoadClientObservabilityMode =
  TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED ? "enabled" : "disabled";

const TOOL_LOAD_BUILD_ATTESTATION_PATTERN =
  /^v1:[a-f0-9]{40}:[A-Za-z0-9._-]{1,80}$/;

export function resolveToolLoadBuildAttestation(value: string | undefined): string {
  return value && TOOL_LOAD_BUILD_ATTESTATION_PATTERN.test(value)
    ? value
    : "unavailable";
}

export const TOOL_LOAD_CLIENT_BUILD_ATTESTATION = resolveToolLoadBuildAttestation(
  process.env.NEXT_PUBLIC_TOOL_LOAD_BUILD_ATTESTATION,
);

export function toolLoadNavPrefetchMode(prefetch: boolean | undefined): "disabled" | "default" {
  return prefetch === false ? "disabled" : "default";
}

export function toolLoadNavigationPrefetch(): false | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.sessionStorage.getItem(TOOL_LOAD_RUNNER_SESSION_KEY) === "1"
      ? false
      : undefined;
  } catch {
    return undefined;
  }
}

export const LIVE_TOOL_SURFACES = [
  "game-lines",
  "fantasy-props",
  "low-hold",
  "arbitrage",
  "ev-sniper",
  "whale-watch",
  "esports-discrepancies",
  "odds-screen",
] as const;

export type LiveToolSurface = (typeof LIVE_TOOL_SURFACES)[number];

export const TRUSTWORTHY_CONTENT_SOURCES = [
  "server-200",
  "client-200",
  "client-304",
  "snapshot-fresh",
  "delta-valid",
  "semantic-empty",
] as const;

export type TrustworthyContentSource = (typeof TRUSTWORTHY_CONTENT_SOURCES)[number];
export type ToolContentSource = TrustworthyContentSource | "degraded-usable";
export type ToolLoadTerminalKind =
  | "trustworthy-content"
  | "degraded-usable"
  | "access-locked"
  | "terminal-error"
  | "expired";

export type ToolContentReceipt = {
  contentId: string;
  surface: LiveToolSurface;
  source?: ToolContentSource;
  terminalKind: Exclude<ToolLoadTerminalKind, "expired">;
  trustworthy: boolean;
  degraded: boolean;
  nocache: boolean;
};

export type ToolContentReceiptInput =
  | {
      surface: LiveToolSurface;
      source: ToolContentSource;
      terminalKind?: never;
      nocache?: boolean;
    }
  | {
      surface: LiveToolSurface;
      source?: never;
      terminalKind: "access-locked" | "terminal-error";
      nocache?: boolean;
    };

export type ToolLoadExclusionReason =
  | "hidden-tab"
  | "hard-navigation"
  | "nocache"
  | "degraded-fallback"
  | "access-locked"
  | "terminal-error"
  | "expired";

export type ToolLoadRouteMatch = {
  surface: LiveToolSurface;
  pathname: string;
};

export type ToolLoadTerminal = {
  kind: ToolLoadTerminalKind;
  atMs: number;
  contentSource?: ToolContentSource;
};

export type ToolRouteCommit = {
  pathname: string;
  atMs: number;
};

export type ActiveToolLoadTrace = {
  state: "active";
  traceId: string;
  targetSurface: LiveToolSurface;
  initialTargetPath: string;
  committedPaths: readonly string[];
  committedPathEntries: readonly ToolRouteCommit[];
  startedAtMs: number;
};

export type FinishedToolLoadTrace = Omit<ActiveToolLoadTrace, "state"> & {
  state: "terminal";
  terminal: ToolLoadTerminal;
};

export type ToolLoadTrace = ActiveToolLoadTrace | FinishedToolLoadTrace;

export function createToolContentReceipt(input: ToolContentReceiptInput): ToolContentReceipt {
  const degraded = input.source === "degraded-usable";
  return {
    contentId: globalThis.crypto.randomUUID(),
    surface: input.surface,
    source: input.source,
    terminalKind: input.terminalKind ?? (degraded ? "degraded-usable" : "trustworthy-content"),
    trustworthy: input.source !== undefined && !degraded,
    degraded,
    nocache: input.nocache ?? false,
  };
}

export type LiveContentOutcome = "client-200" | "semantic-empty" | "access-locked" | "terminal-error";

export function liveContentOutcome(state: {
  readonly locked: boolean;
  readonly blocked: boolean;
  readonly failed: boolean;
  readonly fills: boolean;
  readonly empty: boolean;
  readonly settled: boolean;
}): LiveContentOutcome | null {
  if (state.locked) return "access-locked";
  if (state.blocked) return null;
  if (state.failed) return "terminal-error";
  if (state.fills) return "client-200";
  return state.empty && state.settled ? "semantic-empty" : null;
}

export function liveContentReceipt(surface: LiveToolSurface, outcome: LiveContentOutcome | null): ToolContentReceipt | null {
  if (outcome === null) return null;
  return outcome === "access-locked" || outcome === "terminal-error"
    ? createToolContentReceipt({ surface, terminalKind: outcome })
    : createToolContentReceipt({ surface, source: outcome });
}

export function reconcileToolContentReceipt(
  current: ToolContentReceipt | null,
  input: ToolContentReceiptInput,
  options: { forceNew?: boolean } = {},
): ToolContentReceipt {
  const source = input.source;
  const degraded = source === "degraded-usable";
  const terminalKind = input.terminalKind ?? (degraded ? "degraded-usable" : "trustworthy-content");
  const nocache = input.nocache ?? false;
  if (
    !options.forceNew &&
    current?.surface === input.surface &&
    current.source === source &&
    current.terminalKind === terminalKind &&
    current.nocache === nocache
  ) return current;
  return createToolContentReceipt(input);
}

const EXACT_SURFACE_ROUTES: ReadonlyArray<readonly [pathname: string, surface: LiveToolSurface]> = [
  ["/optimizer/props", "fantasy-props"],
  ["/optimizer", "game-lines"],
  ["/low-hold", "low-hold"],
  ["/arbitrage", "arbitrage"],
  ["/ev-sniper", "ev-sniper"],
  ["/whale-watch", "whale-watch"],
  ["/prop-discrepancies", "esports-discrepancies"],
];

function stripQueryAndHash(value: string): string {
  const queryIndex = value.indexOf("?");
  const hashIndex = value.indexOf("#");
  const cutAt = [queryIndex, hashIndex]
    .filter((index) => index >= 0)
    .reduce((smallest, index) => Math.min(smallest, index), value.length);
  const pathname = value.slice(0, cutAt) || "/";
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function classifyLiveToolPath(value: string): ToolLoadRouteMatch | null {
  const pathname = stripQueryAndHash(value);

  for (const [route, surface] of EXACT_SURFACE_ROUTES) {
    if (pathname === route) return { surface, pathname };
  }

  if (pathname === "/odds-screen" || pathname.startsWith("/odds-screen/")) {
    return { surface: "odds-screen", pathname };
  }

  return null;
}

export function startToolLoadTrace(input: {
  traceId: string;
  targetPath: string;
  startedAtMs: number;
}): ActiveToolLoadTrace | null {
  const match = classifyLiveToolPath(input.targetPath);
  if (!match) return null;

  return {
    state: "active",
    traceId: input.traceId,
    targetSurface: match.surface,
    initialTargetPath: match.pathname,
    committedPaths: [],
    committedPathEntries: [],
    startedAtMs: input.startedAtMs,
  };
}

export function recordToolRouteCommit(
  trace: ToolLoadTrace,
  path: string,
  committedAtMs: number,
): ToolLoadTrace {
  if (trace.state === "terminal") return trace;

  const match = classifyLiveToolPath(path);
  if (
    !match ||
    match.surface !== trace.targetSurface ||
    !Number.isFinite(committedAtMs) ||
    committedAtMs < trace.startedAtMs
  ) return trace;
  if (trace.committedPaths.at(-1) === match.pathname) return trace;
  if (trace.committedPaths.length >= TOOL_LOAD_MAX_ROUTE_COMMITS) return trace;

  return {
    ...trace,
    committedPaths: [...trace.committedPaths, match.pathname],
    committedPathEntries: [
      ...trace.committedPathEntries,
      { pathname: match.pathname, atMs: committedAtMs },
    ],
  };
}

export function finishToolLoadTrace(
  trace: ToolLoadTrace,
  terminal: ToolLoadTerminal,
): FinishedToolLoadTrace {
  if (trace.state === "terminal") return trace;
  return { ...trace, state: "terminal", terminal };
}

export function expireToolLoadTrace(trace: ToolLoadTrace, nowMs: number): ToolLoadTrace {
  if (trace.state === "terminal" || nowMs - trace.startedAtMs < TOOL_LOAD_TRACE_TIMEOUT_MS) {
    return trace;
  }
  return finishToolLoadTrace(trace, { kind: "expired", atMs: nowMs });
}

export function classifyPrimarySample(input: {
  visibilityState: DocumentVisibilityState;
  hardNavigation: boolean;
  nocache: boolean;
  terminal: ToolLoadTerminal;
}): { eligible: true } | { eligible: false; reason: ToolLoadExclusionReason } {
  if (input.visibilityState !== "visible") return { eligible: false, reason: "hidden-tab" };
  if (input.hardNavigation) return { eligible: false, reason: "hard-navigation" };
  if (input.nocache) return { eligible: false, reason: "nocache" };

  switch (input.terminal.kind) {
    case "trustworthy-content":
      return { eligible: true };
    case "degraded-usable":
      return { eligible: false, reason: "degraded-fallback" };
    case "access-locked":
      return { eligible: false, reason: "access-locked" };
    case "terminal-error":
      return { eligible: false, reason: "terminal-error" };
    case "expired":
      return { eligible: false, reason: "expired" };
  }
}

const TOOL_LOAD_METRIC_FIELDS = new Set([
  "milestone",
  "surface",
  "terminal_kind",
  "content_source",
  "eligible",
  "exclusion_reason",
  "cache_outcome",
  "navigation_kind",
  "viewport_class",
  "deployment_environment",
  "duration_ms",
  "commit_count",
  "redirected",
  "paint_method",
]);

const SENSITIVE_FIELD_PATTERN =
  /(?:user|trace|event|player|team|book_list|books|query|search|name|email|token|key|id)$/i;

export function assertBoundedToolLoadMetricFields(properties: Readonly<Record<string, unknown>>): void {
  for (const key of Object.keys(properties)) {
    if (SENSITIVE_FIELD_PATTERN.test(key) || !TOOL_LOAD_METRIC_FIELDS.has(key)) {
      throw new Error(`Unbounded or sensitive tool-load metric field: ${key}`);
    }
  }
}
